// External
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { map, catchError } from 'rxjs/operators';

// Internal
import { MessageService } from '../common/services/core/message.service';
import { PrivacyEvents } from '../common/values/privacy.values.service';
import { ToastService } from '../common/services/global/toast/toast.service';
import { ValuesService, SIDEMENU_COLLAPSE_POINT } from '../common/values/values.service';
import { HideComponentService } from '../common/services/global/hide-component/hide-component.service';
import { outletFadeAnimation } from '../../ux/animations/fade.animations';
import { ProductsConfigService } from '../common/config/products.config.service';
import { ConfigService, LogoutType } from '../common/config/config.service';
import { PushService } from '../common/services/core/push.service';
import { RequestsService } from '../common/services/global/request-service/requests.service';
import { AdobeDataLayerService } from '../common/services/core/adobe.datalayer.service';
import { Outlet, OutletStatus, OutletsStatusService } from '../common/services/core/outlets.status.service';
import { AppComponent } from '../app.component';
import { DrawerModes, Drawers } from '../common/models/Core.model';

@Component({
    selector: 'app-organizer',
    templateUrl: './app.organiser.html',
    styleUrls: ['./app.organiser.scss'],
    animations: [outletFadeAnimation]
})
export class AppOrganiserComponent implements OnInit {
    // drawer object
    drawers = {
        [Drawers.SIDENAV]: {
            open: true,
            collapsePoint: SIDEMENU_COLLAPSE_POINT,
            displaced: false, // cute feature
            mode: DrawerModes.OVER
        },
        [Drawers.NOTIFICATIONS]: {
            open: false
        },
        [Drawers.USER_DETAILS]: {
            open: false
        }
    };
    public readonly Drawers = Drawers;
    user: any = {};

    public getLoadingApp = AppComponent.getLoadingApp;
    public sessionExpired = false;

    /**
     * A change in routeName triggers '@outletFadeAnimation' on the router-outlet which handles that route
     * * targets the changing router-outlet
     * * behaviour is same for all router-outlets under 'main' tag (app.organiser outlet and sub-pages outlets as well)
     * * the name is generated from router's url on NavigationEnd (when dataResolverDone is emited)
     */
    public routeName: string;

    constructor(
        public router: Router,
        private messageService: MessageService,
        private readonly toastService: ToastService,
        public readonly valuesService: ValuesService,
        private translateService: TranslateService,
        public hideCompService: HideComponentService,
        public readonly productsConfigService: ProductsConfigService,
        public readonly configService: ConfigService,
        private readonly pushService: PushService,
        private readonly requestsService: RequestsService,
        private readonly adobeDataLayerService: AdobeDataLayerService,
        private readonly outletsStatusService: OutletsStatusService
    ) {
        this.hideCompService.visibleSideMenu = false;

        this.messageService.getMessage()
        .pipe(
            map((message) => {
                switch (message.text) {
                    case PrivacyEvents.MANUAL_SCAN_DONE:
                        this.toastService.success(this.translateService.instant('privacy.manual.scan.done'));
                        break;
                    case this.valuesService.events.dataResolverDone:
                        this.routeName = this.router.url;
                        if (this.sessionExpired) {
                            this.adobeDataLayerService.setPageSection(this.valuesService.omnitureEvents.sessionExpired, 0, true);
                            this.outletsStatusService.setStatus(Outlet.PAGE, OutletStatus.DONE);
                        }
                        break;
                    case this.valuesService.events.sessionExpired:
                        this.sessionExpired = true;
                        this.adobeDataLayerService.setPageSection(this.valuesService.omnitureEvents.sessionExpired, 0, true);
                        this.hideCompService.showSideMenu();
                        break;
                    default:
                        break;
                }
            }),
            catchError((err) => {
                throw err;
            })
        ).subscribe();
    }

    ngOnInit() {
        this.positionSidenav();
    }

    decodeUri(route) {
        route.url = decodeURIComponent(route.url);
        return route;
    }

    positionSidenav(event?): void {
        // verificam daca window-width-ul este peste punctul de collapse al sidebaru-lui
        // daca este, drawer.sidenav va fi [open]
        this.drawers[Drawers.SIDENAV].open = event ? event.target.innerWidth > this.drawers[Drawers.SIDENAV].collapsePoint : window.innerWidth > this.drawers[Drawers.SIDENAV].collapsePoint;
        // sidenav.displaced este un mic aritificiu dragut, ne putem lipsi daca e in plus sau genereaza probleme :D :P
        // Pe viewport-urile non-desktop (cu width-ul mai mic decat sidenav.collapsePoint) darwer-ul face push de site, fara a-i altera dimensiunea :P
        this.drawers[Drawers.SIDENAV].displaced = !this.drawers[Drawers.SIDENAV].open;
        this.drawers[Drawers.SIDENAV].mode = window.innerWidth > this.drawers[Drawers.SIDENAV].collapsePoint ? DrawerModes.SIDE : DrawerModes.OVER;
    }

    // gets event with user details for header
    public onGetUser(event): void {
        if (event) {
            this.user = event;
        }
    }

    // toggle drawers
    toggleDrawer(target: Drawers): void {
        if (target === Drawers.NOTIFICATIONS) {
            this.drawers[Drawers.USER_DETAILS].open = false;
        } else if (target === Drawers.USER_DETAILS) {
            this.drawers[Drawers.NOTIFICATIONS].open = false;
        }

        this.drawers[target].open = !this.drawers[target].open;

        if (!this.drawers[Drawers.NOTIFICATIONS].open && !this.drawers[Drawers.USER_DETAILS].open) {
            this.messageService.sendMessage(this.valuesService.events.drawer, {opened: false, target: target, mode: this.drawers[target].mode});
        } else {
            this.messageService.sendMessage(this.valuesService.events.drawer, {opened: true, target: target, mode: this.drawers[target].mode});
        }
    }

    // close drawer
    closeDrawers(): void {
        this.messageService.sendMessage(this.valuesService.events.drawer, {opened: false, target: Drawers.ALL});
        for (const drawer in this.drawers) {
            if (this.drawers[drawer].open && (!this.drawers[drawer].mode || (this.drawers[drawer].mode && this.drawers[drawer].mode === DrawerModes.OVER))) {
                this.drawers[drawer].open = false;
            }
        }
    }

    /**
     * Function that sends user to the login page
     * @returns {nothing}
     */
    public relogin(): void {
        this.pushService.unregisterPush();
        this.requestsService.logout(LogoutType.REDIRECT_TO_RELOGIN_PAGE).subscribe();
    }
}